/**
 * Batch Review "Export to Excel" — the pure row transformation.
 *
 * Extracted from BatchFilingReview.tsx so the date handling can be tested. The export is hand-built
 * rather than taken from the grid's columns, so it does NOT inherit the grid's valueFormatters: every
 * value it emits has to be formatted here or it goes into the sheet exactly as the API sent it. That is
 * how QA came to see `2026-08-31T00:00:00.000Z` in the Due Date column.
 *
 * ⚠️ DATE-ONLY AND TIMESTAMP ARE NOT THE SAME FIELD AND MUST NOT GET THE SAME TREATMENT.
 *   Due Date        — a business date. Time is meaningless and must not appear.
 *   Extraction Date — a real instant. Its time is information and is kept.
 * Stripping the time from everything would have silently destroyed the second one, which is why this
 * is per field rather than one pass over anything that parses as a date.
 *
 * ⚠️ STRINGS, NOT NATIVE DATES. `writeXlsxFile` would accept a Date, but these values arrive as
 * midnight UTC — handing Excel a native Date renders them in the viewer's local zone, so anyone behind
 * UTC sees the PREVIOUS day. A wrong date that looks right is worse than a right date that does not
 * sort as one. `CustomerInbox` already exports preformatted strings for the same reason.
 */

import { formatUtcToLocal } from './utils';

/** The exported columns, in order. Kept beside the row builder so the two cannot drift apart. */
export const BATCH_REVIEW_EXPORT_HEADERS = [
    'Insured Name', 'Policy Number', 'Transaction Type', 'Status', 'Filing Status', 'Coverage Code',
    'Premium', 'Tax', 'Physical Risk Address', 'Extraction Date', 'Due Date', 'Exceptions',
] as const;

/**
 * A DATE-ONLY business date as `MM-DD-YYYY`.
 *
 * Reads the UTC components deliberately: these values are midnight UTC, so using the local getters
 * would roll the date back a day for any viewer west of UTC. This is the same function the grid's date
 * columns use — shared rather than copied, so the spreadsheet and the screen cannot disagree about
 * what day a filing is due.
 */
export function formatFilingDate(value: string | number): string {
    const raw = String(value).trim();
    if (!raw) return raw;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${mm}-${dd}-${yyyy}`;
}

/**
 * A TRUE TIMESTAMP, local, with its time kept.
 *
 * Guards empty explicitly: `formatUtcToLocal` answers the literal string `N/A` for null, which would
 * otherwise be written into the cell as though it were data.
 */
export function formatExportTimestamp(value: string | number | null | undefined): string {
    if (value === null || value === undefined || String(value).trim() === '') return '';
    const formatted = formatUtcToLocal(value, 'MM-DD-YYYY hh:mm A');
    return formatted === 'N/A' || formatted === 'Invalid date' ? '' : formatted;
}

/** Only the fields the export reads. Structural, so this helper does not depend on the page's types. */
export interface BatchReviewExportRow {
    insuredName?: string | null;
    policyNumber?: string | null;
    transactionType?: string | null;
    status?: string | null;
    coverageCode?: string | null;
    premium?: string | number | null;
    tax?: string | number | null;
    physicalRiskAddress?: string | null;
    extractionDate?: string | null;
    dueDate?: string | null;
    fieldExceptions?: { message?: string | null }[] | null;
}

/**
 * One spreadsheet row per policy, in header order.
 *
 * `filingStatusLabel` is injected rather than imported so this stays pure: the label is the same one
 * the grid's chip shows, and the page owns that mapping.
 */
export function buildBatchReviewExportRows(
    policies: BatchReviewExportRow[],
    filingStatusLabel: (row: BatchReviewExportRow) => string,
): (string | number | null | undefined)[][] {
    return policies.map((r) => [
        r.insuredName,
        r.policyNumber,
        r.transactionType,
        r.status,
        filingStatusLabel(r),
        r.coverageCode,
        r.premium,
        r.tax,
        r.physicalRiskAddress,
        // TRUE TIMESTAMP — time retained.
        formatExportTimestamp(r.extractionDate),
        // DATE-ONLY — no time component may survive.
        r.dueDate ? formatFilingDate(r.dueDate) : '',
        (r.fieldExceptions || []).map((e) => e?.message).filter(Boolean).join(' | '),
    ]);
}
